'use client';

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PageHero } from '@/components/shared/PageHero';
import { SectionLabel } from '@/components/shared/SectionLabel';
import { InsightCard } from '@/components/shared/InsightCard';
import { RichText, richTextArticleClass } from '@/components/shared/RichText';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Target } from 'lucide-react';
import { MediaImg } from '@/components/shared/MediaImg';
import { stripHtml } from '@/lib/utils';
import { getServiceCategoryBySlug } from '@/lib/actions/service-categories';
import { getInsightBySlug, getInsights } from '@/lib/actions/insights';
import NotFoundPage from './NotFoundPage';

export default function InsightDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [mode, setMode] = useState<'article' | 'category' | 'notfound' | null>(null);
  const [article, setArticle] = useState<any>(null);
  const [serviceCategory, setServiceCategory] = useState<any>(null);
  const [categoryArticles, setCategoryArticles] = useState<any[]>([]);

  useEffect(() => {
    if (!slug) return;

    void (async () => {
      const insightData = await getInsightBySlug(slug);
      if (insightData?.article) {
        setArticle(insightData.article);
        setMode('article');
        return;
      }

      const category = await getServiceCategoryBySlug(slug);
      if (category) {
        const allInsights = await getInsights();
        const filtered = allInsights
          .filter((item: any) => item.categoryId === category.id)
          .sort((a: any, b: any) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime());
        setServiceCategory(category);
        setCategoryArticles(filtered);
        setMode('category');
        return;
      }

      setMode('notfound');
    })();
  }, [slug]);

  if (!mode) return null;
  if (mode === 'notfound') return <NotFoundPage />;

  if (mode === 'article' && article) {
    const categoryName = article.serviceCategory?.name || 'Insights';
    const cleanDescription = stripHtml(article.summary || article.content || '').substring(0, 160);
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      image: article.coverImage ? [article.coverImage] : [],
      datePublished: new Date(article.publishDate).toISOString(),
      author: article.author ? [{ '@type': 'Person', name: article.author }] : [],
      publisher: {
        '@type': 'Organization',
        name: 'Simisi&Co.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://simisi.id/images/simisi/logo-simisi.svg',
        },
      },
      description: cleanDescription,
    };

    return (
      <>
        <Helmet>
          <title>{article.title}</title>
          <meta name="description" content={cleanDescription} />
          <meta property="og:title" content={article.title} />
          <meta property="og:description" content={cleanDescription} />
          <meta property="og:type" content="article" />
          {article.coverImage && <meta property="og:image" content={article.coverImage} />}
        </Helmet>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
        <PageHero
          title={article.title}
          subtitle={categoryName}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Insights', href: '/insights' },
            ...(article.serviceCategory
              ? [{ label: article.serviceCategory.name, href: `/insights/${article.serviceCategory.slug}` }]
              : []),
            { label: article.title },
          ]}
        />
        <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
          <div className="max-w-[800px] mx-auto">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-gray text-sm mb-8 font-sans">
              <span>{format(new Date(article.publishDate), 'MMMM dd, yyyy')}</span>
              {article.author && (
                <>
                  <span className="w-1 h-1 rounded-full bg-light hidden sm:block" />
                  <span>By {article.author}</span>
                </>
              )}
              {article.footprints?.length > 0 && (
                <div className="flex flex-wrap gap-2 items-center">
                  <span className="w-1 h-1 rounded-full bg-light hidden sm:block" />
                  <div className="flex gap-2">
                    {article.footprints.map((f: any) => (
                      <Badge
                        key={f.id}
                        variant="secondary"
                        className="bg-navy/5 text-navy border-none flex gap-1 items-center px-3 py-1"
                      >
                        <Target size={12} className="text-primary" />
                        {f.title}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {article.coverImage && (
              <div className="aspect-video rounded-xl overflow-hidden mb-10 border border-light shadow-lg">
                <MediaImg src={article.coverImage} alt={article.title} className="w-full h-full object-cover" />
              </div>
            )}

            <RichText html={article.content || ''} className={richTextArticleClass} />
          </div>
        </section>
      </>
    );
  }

  if (mode === 'category' && serviceCategory) {
    const description = stripHtml(serviceCategory.description || '').substring(0, 160);

    return (
      <>
        <Helmet>
          <title>{`${serviceCategory.name} Insights`}</title>
          <meta
            name="description"
            content={description || `Explore insights and perspectives on ${serviceCategory.name}.`}
          />
        </Helmet>
        <PageHero
          title={`${serviceCategory.name} Insights`}
          subtitle={serviceCategory.description ? stripHtml(serviceCategory.description).substring(0, 120) : undefined}
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Insights', href: '/insights' },
            { label: serviceCategory.name },
          ]}
        />
        <section className="py-10 md:py-[72px] px-5 md:px-[40px] bg-white">
          <div className="max-w-[1170px] mx-auto">
            {serviceCategory.description && (
              <div className="mb-12 max-w-3xl">
                <RichText
                  html={serviceCategory.description}
                  className="text-[16px] text-gray-600 leading-relaxed font-sans [&_p]:mb-4"
                />
              </div>
            )}
            <SectionLabel text="Insights" />
            <h2 className="text-[32px] md:text-[40px] font-bold text-navy mb-12 font-heading">
              Latest from {serviceCategory.name}
            </h2>

            {categoryArticles.length === 0 ? (
              <div className="py-20 text-center text-gray font-sans">
                No articles found in this category yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {categoryArticles.map((item) => (
                  <InsightCard
                    key={item.slug}
                    slug={item.slug}
                    title={item.title}
                    description={item.summary || item.content || ''}
                    category={item.serviceCategory?.name}
                    date={format(new Date(item.publishDate), 'MMM dd, yyyy')}
                    image={item.coverImage || ''}
                    footprints={item.footprints}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </>
    );
  }

  return <NotFoundPage />;
}
